import { Routes, Route } from 'react-router-dom'
import ImporterDetailsPage from './pages/importer-details'
import useI18n from '@/shared/hooks/usei18n'
import { useSession } from '@/shared/hooks/useSession'
import useDialog from '@/common/hooks/useDialog'
import { useGetAllImporters } from '@/features/importers/hooks/importers'
import { Container, LoadingScreen } from '@/shared/components'
import { Button, H2, Table, TableColumnHeader, TableRow } from '@/common'
import ImporterCreateDialog from './dialogs/ImporterCreateDialog'
import DeleteImporterDialog from './dialogs/DeleteImporterDialog'
import MissingResourcesAlert from '@/shared/components/MissingResourcesAlert'
import ImporterRow from './components/ImporterRow'
import { Importer } from '@/features/importers/hooks/importers'
import { useState } from 'react'

export default function Importers() {
  return (
    <Routes>
      <Route path="/:importerId">
        <Route index path="*" element={<ImporterDetailsPage />} />
      </Route>
      <Route path="/" element={<ImportersPage />} />
    </Routes>
  )
}

function ImportersPage() {
  const { t } = useI18n()
  const organizationId = useSession().session.organization.id
  const {
    open: openCreateImporter,
    openDialog: openCreateImporterDialog,
    closeDialog: closeCreateImporterDialog
  } = useDialog()
  const {
    open: isDeleteImporterDialogOpen,
    openDialog: openDeleteImporterDialog,
    closeDialog: closeDeleteImporterDialog
  } = useDialog()
  const [selectedImporter, setSelectedImporter] = useState<Importer | null>(null)

  const importersQuery = useGetAllImporters(organizationId)

  const isLoadingImporters = importersQuery.isLoading
  const importers = importersQuery.data ?? []

  if (isLoadingImporters) {
    return <LoadingScreen />
  }

  return (
    <Container>
      <header className="flex justify-between items-center">
        <H2>{t('importers:importersPage.title')}</H2>
        {importers.length > 0 && (
          <Button onClick={openCreateImporterDialog}>
            {t('importers:importersPage.importersList.noImporters.action')}
          </Button>
        )}
      </header>
      {importers.length === 0 ? (
        <MissingResourcesAlert
          title={t('importers:importersPage.importersList.noImporters.title')}
          description={t('importers:importersPage.importersList.noImporters.description')}
          action={{
            label: t('importers:importersPage.importersList.noImporters.action'),
            onClick: openCreateImporterDialog
          }}
        />
      ) : (
        <div className="overflow-x-auto w-full">
          <Table className="table-fixed">
            <thead>
              <TableRow>
                <TableColumnHeader className="w-48">
                  {t('importers:importersPage.importersList.importerTable.headers.importerName')}
                </TableColumnHeader>
                <TableColumnHeader className="w-36">
                  {t('importers:importersPage.importersList.importerTable.headers.eoriNumber')}
                </TableColumnHeader>
                <TableColumnHeader className="w-36">
                  {t('importers:importersPage.importersList.importerTable.headers.customId')}
                </TableColumnHeader>
                <TableColumnHeader className="w-36">
                  {t('importers:importersPage.importersList.importerTable.headers.location')}
                </TableColumnHeader>
                <TableColumnHeader className="w-36" />
              </TableRow>
            </thead>
            <tbody>
              {importers.map(importer => (
                <ImporterRow
                  key={importer.id}
                  importer={importer}
                  onDeleteClick={() => {
                    setSelectedImporter(importer)
                    openDeleteImporterDialog()
                  }}
                />
              ))}
            </tbody>
          </Table>
        </div>
      )}
      <ImporterCreateDialog
        isOpen={openCreateImporter}
        close={closeCreateImporterDialog}
        organizationId={organizationId}
      />
      {selectedImporter && (
        <DeleteImporterDialog
          isOpen={isDeleteImporterDialogOpen}
          close={closeDeleteImporterDialog}
          importerName={selectedImporter.name}
          organizationId={organizationId}
          importerId={selectedImporter.id}
          onAfterLeave={() => setSelectedImporter(null)}
        />
      )}
    </Container>
  )
}
